import { useMemo, useState } from 'react';

import { useGetFeaturesQuery } from '@features/tenants';
import { useGetProjectUsersQuery } from '@features/users';
import {
  BarChart as BarChartIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router';

export const SIDEBAR_WIDTH = 180;
export const SIDEBAR_COLLAPSED_WIDTH = 58;

interface SidebarProps {
  open: boolean;
  appId: string;
  tenantId: string;
  onHover?: (hovering: boolean) => void;
}

interface NavItem {
  title: string;
  icon: React.ReactNode;
  route?: string;
  children?: Array<{ title: string; route: string }>;
}

/**
 * Left navigation sidebar with collapsible menu
 */
export const Sidebar = ({ open, appId, tenantId, onHover }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>(['Overview']);

  // Fetch project users to get team count
  const { data: projectUsers = [] } = useGetProjectUsersQuery(
    { tenantId, appId },
    { skip: !tenantId || !appId }
  );

  // Fetch tenant features for conditional menu items
  const { data: features = [] } = useGetFeaturesQuery(tenantId, {
    skip: !tenantId,
  });

  // Check if "lingkapis" feature is enabled (matches dataProtect sidenav.js)
  const hasLingkApis = features.some((f) => f.key === 'lingkapis');

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        title: 'Overview',
        icon: <DashboardIcon />,
        children: [
          { title: 'Dashboard', route: `/a/${appId}/dash` },
          {
            title: `Team${projectUsers.length > 0 ? ` (${projectUsers.length})` : ''}`,
            route: `/a/${appId}/dash/team`,
          },
          { title: 'Settings', route: `/a/${appId}/dash/settings` },
        ],
      },
      {
        title: 'Environments',
        icon: <ShareIcon />,
        route: `/a/${appId}/env`,
      },
      {
        title: 'Recipes',
        icon: <DescriptionIcon />,
        route: `/a/${appId}/tf`,
      },
      // Wizards is commented out in dataProtect sidenav.js - not shown in sidebar
      {
        title: 'Executions',
        icon: <HistoryIcon />,
        route: `/a/${appId}/health`,
      },
      {
        title: 'Events',
        icon: <EventIcon />,
        children: [
          { title: 'Subscriptions', route: `/a/${appId}/events` },
          { title: 'Manage Objects', route: `/a/${appId}/events/obj` },
        ],
      },
      {
        title: 'Schedules',
        icon: <ScheduleIcon />,
        route: `/a/${appId}/schedule`,
      },
      {
        title: 'Analytics',
        icon: <BarChartIcon />,
        route: `/a/${appId}/ca`,
      },
    ];

    // Lingk APIs is conditional on "lingkapis" feature flag (matches dataProtect sidenav.js)
    if (hasLingkApis) {
      items.push({
        title: 'Lingk APIs',
        icon: <StorageIcon />,
        route: `/a/${appId}/db`,
      });
    }

    return items;
  }, [appId, projectUsers.length, hasLingkApis]);

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      setExpandedItems((prev) =>
        prev.includes(item.title) ? prev.filter((t) => t !== item.title) : [...prev, item.title]
      );
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const handleChildClick = (route: string) => {
    navigate(route);
  };

  const isSelected = (route?: string) => {
    if (!route) return false;
    return location.pathname.startsWith(route);
  };

  const isChildSelected = (children?: Array<{ route: string }>) => {
    if (!children) return false;
    return children.some((child) => location.pathname.startsWith(child.route));
  };

  return (
    <Drawer
      variant="permanent"
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      sx={{
        width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#404040',
          borderRight: 'none',
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
          top: 50, // Below header
          height: 'calc(100% - 50px)',
        },
      }}
    >
      {/* Home button */}
      <List sx={{ pt: 1 }}>
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Home'} placement="right">
            <ListItemButton
              onClick={() => navigate('/')}
              sx={{
                minHeight: 45,
                justifyContent: open ? 'initial' : 'center',
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <HomeIcon />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Home"
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: 'white',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', mx: 1, mb: 1 }} />

      {/* Navigation items */}
      <List sx={{ pt: 0 }}>
        {navItems.map((item) => (
          <Box key={item.title}>
            <ListItem disablePadding>
              <Tooltip title={open ? '' : item.title} placement="right">
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  selected={isSelected(item.route) || isChildSelected(item.children)}
                  sx={{
                    minHeight: 45,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#6C63FF',
                      '&:hover': {
                        backgroundColor: '#5a52e0',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 'auto',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <>
                      <ListItemText
                        primary={item.title}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                      {item.children &&
                        (expandedItems.includes(item.title) ? (
                          <ExpandLess sx={{ color: 'white' }} />
                        ) : (
                          <ExpandMore sx={{ color: 'white' }} />
                        ))}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            {/* Child items */}
            {item.children && open && (
              <Collapse in={expandedItems.includes(item.title)} timeout="auto">
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.route}
                      onClick={() => handleChildClick(child.route)}
                      selected={location.pathname === child.route}
                      sx={{
                        pl: 5,
                        minHeight: 36,
                        '&.Mui-selected': {
                          backgroundColor: 'transparent',
                          '& .MuiListItemText-primary': {
                            fontWeight: 700,
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={child.title}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '0.8125rem',
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Drawer>
  );
};
